import * as React from 'react';
import {
    Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Grid, Link, Stack, Switch, Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
    BETUP_FEED_LABELS, getBetupFeedReviewSettings, getBetupFeedReviewStats, updateBetupFeedReviewSettings,
} from '../Api';
import {
    BetupFeedReviewPeriodStatsDto, BetupFeedReviewSettingsDto, BetupFeedReviewStatsDto,
} from '../models/dtos/BetupFeedReviewDto';
import { formatCount, formatUsd, loadErrorText } from '../components/betupFeed/BetupFeedShared';
import FeedHotMatchSettingCard from '../components/betupFeed/FeedHotMatchSettingCard';

const PERIOD_TITLES: Record<string, string> = {
    LAST_24_HOURS: 'Last 24 hours',
    LAST_7_DAYS: 'Last 7 days',
    LAST_30_DAYS: 'Last 30 days',
};
const PERIOD_ORDER = ['LAST_24_HOURS', 'LAST_7_DAYS', 'LAST_30_DAYS'];

/** The AI hub task that reviews the posts: its prompt (policy), model and cost are edited in AI › Tasks. */
const REVIEW_TASK_CODE = 'moderation.feed-post';

function StatLine({ label, value }: { label: string; value: string }) {
    return (
        <Stack direction="row" justifyContent="space-between" gap={2}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
        </Stack>
    );
}

function PeriodCard({ stats }: { stats: BetupFeedReviewPeriodStatsDto }) {
    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="subtitle2" color="text.secondary">{PERIOD_TITLES[stats.period] ?? stats.period}</Typography>
                <Typography variant="h4" sx={{ my: 1 }}>{formatUsd(stats.cost_usd, 4)}</Typography>
                <Stack gap={0.5}>
                    <StatLine label="Reviews" value={formatCount(stats.reviews)} />
                    <StatLine label="Approved" value={formatCount(stats.approved)} />
                    <StatLine label="Rejected" value={formatCount(stats.rejected)} />
                    <StatLine label="Input tokens" value={formatCount(stats.input_tokens)} />
                    <StatLine label="Output tokens" value={formatCount(stats.output_tokens)} />
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function BetupFeedReviewPage() {
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [enabled, setEnabled] = React.useState(true);
    const [settingsLoaded, setSettingsLoaded] = React.useState(false);
    const [stats, setStats] = React.useState<BetupFeedReviewStatsDto | null>(null);
    const [statsLoading, setStatsLoading] = React.useState(true);
    const [error, setError] = React.useState<string | null>(null);
    const [statsError, setStatsError] = React.useState<string | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);

    const applySettings = (dto: BetupFeedReviewSettingsDto) => {
        setEnabled(dto.enabled);
        setSettingsLoaded(true);
    };

    const loadStats = React.useCallback(async () => {
        setStatsLoading(true);
        const dto = await getBetupFeedReviewStats();
        setStatsLoading(false);
        if (dto == null) {
            setStatsError(loadErrorText(BETUP_FEED_LABELS.reviewStats, 'Failed to load review stats.'));
            return;
        }
        setStatsError(null);
        setStats(dto);
    }, []);

    React.useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const dto = await getBetupFeedReviewSettings();
            if (cancelled) return;
            setLoading(false);
            if (dto == null) {
                setError(loadErrorText(BETUP_FEED_LABELS.reviewSettings, 'Failed to load review settings.'));
                return;
            }
            applySettings(dto);
        })();
        loadStats();
        return () => { cancelled = true; };
    }, [loadStats]);

    const onSave = async () => {
        setNotice(null);
        setSaving(true);
        const result = await updateBetupFeedReviewSettings({ enabled });
        setSaving(false);
        if (result.error != null) {
            setError(`Failed to save: ${result.error}`);
            return;
        }
        setError(null);
        if (result.data != null) applySettings(result.data);
        setNotice('Review settings saved.');
    };

    const periods = React.useMemo(() => {
        const list = stats?.periods ?? [];
        return [...list].sort((a, b) => {
            const ia = PERIOD_ORDER.indexOf(a.period);
            const ib = PERIOD_ORDER.indexOf(b.period);
            return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
        });
    }, [stats]);

    return (
        <Stack gap={3} sx={{ mx: 5, my: 5 }}>
            <Typography variant="h4">BETUP Feed — post review</Typography>
            <Typography variant="body2" color="text.secondary">
                Every player post is created pending and reviewed by the AI hub (text and image) before it is published to the
                feed. Any category (offensive, sexual, racist, violent, illegal, real-money questions, other) rejects the
                post and the author sees why. Pending and rejected posts can also be decided by hand on the Posts page.
                Key: feed.review.enabled.
            </Typography>

            {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            <Typography variant="h6">Settings</Typography>
            {loading ? (
                <Box><CircularProgress /></Box>
            ) : settingsLoaded ? (
                <Grid container spacing={2} maxWidth="md">
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={<Switch checked={enabled} onChange={e => setEnabled(e.target.checked)} />}
                            label={enabled ? 'Review enabled' : 'Review disabled'}
                        />
                        {!enabled && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                With review off, new posts are published immediately without any check (no model call).
                            </Alert>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Alert severity="info">
                            The moderation policy (system prompt), the model and the cost of each review are the AI hub task{' '}
                            <Link component={RouterLink} to={`/ai/tasks/${REVIEW_TASK_CODE}`} sx={{ fontFamily: 'monospace' }}>
                                {REVIEW_TASK_CODE}
                            </Link>
                            {' '}— edit them in AI › Tasks. Keep the policy naming the categories OFFENSIVE, SEXUAL, RACIST,
                            VIOLENT, ILLEGAL, REAL_MONEY_FAQ, OTHER: the verdict format is the task's output schema.
                        </Alert>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={onSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </Button>
                    </Grid>
                </Grid>
            ) : null}

            <Typography variant="h6" sx={{ mt: 2 }}>Feed</Typography>
            <FeedHotMatchSettingCard />

            <Stack direction="row" alignItems="center" gap={2} sx={{ mt: 2 }}>
                <Typography variant="h6">Cost &amp; usage</Typography>
                <Button size="small" onClick={loadStats} disabled={statsLoading}>Refresh</Button>
                {statsLoading && <CircularProgress size={20} />}
            </Stack>
            {statsError && <Alert severity="error">{statsError}</Alert>}
            {stats != null && (
                <>
                    <Card variant="outlined" sx={{ maxWidth: 320 }}>
                        <CardContent>
                            <Typography variant="body2" color="text.secondary">Posts waiting for review</Typography>
                            <Typography variant="h4" sx={{ mt: 0.5 }}>{formatCount(stats.pending_count)}</Typography>
                        </CardContent>
                    </Card>
                    <Grid container spacing={2}>
                        {periods.map(p => (
                            <Grid item xs={12} md={4} key={p.period}>
                                <PeriodCard stats={p} />
                            </Grid>
                        ))}
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                        Cost as the AI hub priced each review (the model's prices at the time). Every recorded review counts,
                        including re-reviews after a post is edited.
                    </Typography>
                </>
            )}
        </Stack>
    );
}
